import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../data/api";
import type { Route, Vehicle, User, Warehouse } from "../../data/schemaTypes";

// Fuente de datos común para todas las vistas de Última Milla (web).
//
// La unidad de tracking es el **vehículo** (placa), no el chofer. Razón:
// el Excel de planificación trae la placa de cada ruta pero no siempre
// trae el chofer asignado. El admin asocia un chofer al vehículo desde
// Maestros → Vehículos; mientras tanto la ruta del vehículo igual debe
// mostrarse en operación para que el supervisor sepa que existe y a qué
// vehículo seguir.
//
// El chofer queda como metadata opcional (nombre + GPS último); cuando
// no hay chofer asignado, mostramos "sin chofer" pero la ruta y los
// stops se ven normalmente.

export type FleetAlert = "none" | "behindSchedule" | "gpsLost";

/// Evento de cierre de stop — un POD del día, con la hora exacta del
/// cierre y su resultado operativo. La timeline horizontal de
/// FleetPage pinta un círculo coloreado por cada uno.
export type PodEventKind = "delivered" | "partial" | "failed" | "skipped";
export type PodEvent = {
  at: Date;
  kind: PodEventKind;
};

export type LiveVehicle = {
  /// Identificador estable. Usamos vehicle_id porque es el "sujeto"
  /// del seguimiento — un vehículo puede pasar de un chofer a otro y
  /// la ruta sigue siendo la misma.
  id: string;
  plate: string;
  // Chofer asignado al vehículo (opcional — viene del current_driver_user_id).
  driverUserId?: string;
  driverFullName?: string;
  driverCode?: string;
  driverPhone?: string;
  // GPS último del chofer asignado. Si el vehículo no tiene chofer,
  // estos quedan undefined (no hay GPS sin device del chofer).
  lat?: number;
  lon?: number;
  lastGpsAt?: Date;
  // Ubicación del almacén del vehículo — fallback para mostrar el
  // vehículo en el mapa cuando no hay GPS reciente del chofer. La UI
  // lo dibuja con un ícono "stale" para distinguir de una posición
  // real reportada.
  warehouseLat?: number;
  warehouseLon?: number;
  // Hora del primer y último pedido atendido del día (delivered,
  // partial, failed o skipped). El supervisor las usa como "hora real
  // de inicio/fin de operación" en lugar de routeStartedAt/FinishedAt,
  // que dependen de que el chofer marque manualmente esos estados.
  firstStopAt?: Date;
  lastStopAt?: Date;
  // Lista completa de eventos POD del día (delivered, partial,
  // failed, skipped) con timestamp del momento del POD. Sirve para
  // dibujar la timeline horizontal en la pantalla "Vehículos en
  // ruta" — un círculo por evento sobre el eje de horas.
  podEvents: PodEvent[];
  // Ubicación del último pedido atendido — fuente principal para el
  // marker del vehículo en el mapa de supervisión. Más informativo
  // que el GPS (que puede ser un punto intermedio cualquiera) porque
  // muestra hasta dónde efectivamente llegó la operación.
  lastStopLat?: number;
  lastStopLon?: number;
  // Resumen de la ruta del día (siempre presente — el vehículo aparece
  // en `LiveFleetState.vehicles` solo si tiene ruta).
  routeId: string;
  routeCode: string;
  routeStatus: string;
  warehouseId: string;
  routeStartedAt?: Date;
  routeFinishedAt?: Date;
  stopsTotal: number;
  stopsDone: number;        // delivered + partial
  stopsFailed: number;      // failed + skipped
  stopsVisited: number;     // done + failed
};

export type LiveFleetState = {
  vehicles: LiveVehicle[];
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
};

// --- Helpers operativos (mantenemos los nombres legacy `driver*` para
// no romper imports; trabajan sobre LiveVehicle y describen la operación
// del día). Cuando renombremos consumers podemos alias a vehicle*. ---

export function driverProgress(v: LiveVehicle): number {
  if (v.stopsTotal === 0) return 0;
  return (v.stopsDone + v.stopsFailed) / v.stopsTotal;
}

export function driverGpsFresh(v: LiveVehicle): boolean {
  if (!v.lastGpsAt) return false;
  return Date.now() - v.lastGpsAt.getTime() < 5 * 60 * 1000;
}

export function driverAlert(v: LiveVehicle): FleetAlert {
  if (v.routeStatus !== "in_progress") return "none";
  // Alerta gpsLost se eliminó cuando se desactivó el GPS del chofer
  // (ya no hay pings que medir). Solo queda behindSchedule.
  if (v.routeStartedAt) {
    const elapsedH = (Date.now() - v.routeStartedAt.getTime()) / 3_600_000;
    if (elapsedH >= 3 && driverProgress(v) < 0.5) return "behindSchedule";
  }
  return "none";
}

// Ventana ayer..mañana para absorber desfase de timezone entre device
// (hora local) y server (UTC) — mismo criterio que la app móvil.
function dateWindow(plannedDate?: string): { from: string; to: string } {
  const f = (d: Date) => d.toISOString().slice(0, 10);
  const ref = plannedDate ? new Date(plannedDate + "T00:00:00Z") : new Date();
  const prev = new Date(ref.getTime() - 86_400_000);
  const next = new Date(ref.getTime() + 86_400_000);
  return { from: f(prev), to: f(next) };
}

/// Construye la lista de vehículos en operación a partir de:
///   - /api/routes (filtradas por fecha + warehouses opcional)
///   - /api/vehicles (para resolver placas y choferes asignados)
///   - /api/drivers/{id}/last-gps (para el último ping del chofer)
///   - /api/routes/{id} (para conteos de stops por status)
///
/// El parámetro `mode` controla qué subconjunto procesar:
///   - "CONSUMO": rutas con vehicle_id (flujo planificado del día);
///     cada fila representa un vehículo + su chofer asignado.
///   - "SALUD":   rutas self-dispatch del repartidor (vehicle_id NULL,
///     driver_user_id apunta al repartidor). Se trata como si fuera un
///     "vehículo virtual" — `plate` = DNI, `driverFullName` = nombre
///     del repartidor — para que toda la UI existente (cards, mapa,
///     timeline) funcione sin distinguir tipo. Ventana ampliada a los
///     últimos 3 días porque el repartidor sigue trabajando pedidos
///     viejos hasta que los cierra.
async function fetchFleet(
  plannedDate?: string,
  warehouseIds?: string[],
  mode: "CONSUMO" | "SALUD" = "CONSUMO",
): Promise<LiveVehicle[]> {
  if (mode === "SALUD") {
    return fetchSaludFleet(warehouseIds);
  }

  const { from, to } = dateWindow(plannedDate);
  const [routes, vehicles, drivers, warehouses] = await Promise.all([
    api.listRoutes({ fromDate: from, toDate: to, limit: 500, warehouseIds }),
    api.listVehicles(),
    api.listDrivers(),
    api.listWarehouses(),
  ]);

  // Filtramos por la fecha exacta — el window ±1 día es solo para
  // tolerar UTC drift en el backend; la lista visible es solo HOY (o
  // la fecha pedida). Sin esto, el polling del día puede mostrar también
  // rutas de ayer/mañana que están en el window.
  //
  // Comparamos los primeros 10 chars de planned_date — algunos backends
  // devuelven YYYY-MM-DD pero otros devuelven ISO completo
  // (YYYY-MM-DDT00:00:00Z). Slice asegura match correcto en ambos casos.
  const target = (plannedDate ?? new Date().toISOString().slice(0, 10)).slice(0, 10);
  const sameDay = routes.filter((r) => (r.planned_date ?? "").slice(0, 10) === target);

  const vehicleById = new Map<string, Vehicle>();
  vehicles.forEach((v) => vehicleById.set(v.id, v));
  const driverById = new Map<string, User>();
  drivers.forEach((d) => driverById.set(d.id, d));
  const warehouseById = new Map<string, Warehouse>();
  warehouses.forEach((w) => warehouseById.set(w.id, w));

  return Promise.all(
    sameDay
      .filter((r): r is Route & { vehicle_id: string } => !!r.vehicle_id)
      .map((r) => enrichVehicleRow(r, vehicleById, driverById, warehouseById)),
  );
}

/// fetchSaludFleet — fetcher dedicado para el flujo SALUD.
///
/// Diferencias clave vs CONSUMO:
///   - Las rutas a procesar son las self-dispatch del repartidor: tienen
///     vehicle_id = NULL y driver_user_id = id del repartidor.
///   - Ventana extendida a 3 días previos + 1 día buffer hacia adelante
///     (los pedidos quedan abiertos varios días).
///   - El "sujeto" del seguimiento es el repartidor, no un vehículo.
///     Pivoteamos al shape `LiveVehicle` poniendo el DNI en `plate` y
///     el nombre del repartidor en `driverFullName`; el resto de la UI
///     funciona sin tocarse.
async function fetchSaludFleet(
  warehouseIds?: string[],
): Promise<LiveVehicle[]> {
  // Ventana: 3 días hacia atrás, 1 día hacia adelante para tolerar
  // desfase de zonas (mismo criterio que el provider del mobile).
  const f = (d: Date) => d.toISOString().slice(0, 10);
  const now = new Date();
  const from = f(new Date(now.getTime() - 3 * 86_400_000));
  const to = f(new Date(now.getTime() + 86_400_000));

  const [routes, repartidores, warehouses] = await Promise.all([
    api.listRoutes({ fromDate: from, toDate: to, limit: 500, warehouseIds }),
    api.listUsers("repartidor"),
    api.listWarehouses(),
  ]);

  const repartidorById = new Map<string, User>();
  repartidores.forEach((u) => repartidorById.set(u.id, u));
  const warehouseById = new Map<string, Warehouse>();
  warehouses.forEach((w) => warehouseById.set(w.id, w));

  // Una ruta es "self-dispatch SALUD" si no tiene vehículo pero sí
  // chofer (= repartidor que escaneó al menos un pedido). El filtro por
  // vehicle_id == null la diferencia de las rutas planificadas CONSUMO.
  const saludRoutes = routes.filter(
    (r): r is Route & { driver_user_id: string } =>
      !r.vehicle_id && !!r.driver_user_id,
  );

  return Promise.all(
    saludRoutes.map((r) => enrichRepartidorRow(r, repartidorById, warehouseById)),
  );
}

/// Junta route + vehicle + driver (opcional) + detalle de stops + GPS último.
/// Si algo falla (detalle de ruta no accesible, GPS 404), se reporta con
/// los campos opcionales en undefined sin romper la fila.
async function enrichVehicleRow(
  r: Route & { vehicle_id: string },
  vehicleById: Map<string, Vehicle>,
  driverById: Map<string, User>,
  warehouseById: Map<string, Warehouse>,
): Promise<LiveVehicle> {
  const vehicle = vehicleById.get(r.vehicle_id);
  const driverUserId = r.driver_user_id ?? vehicle?.current_driver_user_id;
  const driver = driverUserId ? driverById.get(driverUserId) : undefined;
  const warehouse = warehouseById.get(r.warehouse_id);

  let stopsTotal = 0, stopsDone = 0, stopsFailed = 0;
  // Acumuladores de "hora real de operación" — primer y último pedido
  // atendido del día. delivery.delivered_at gana cuando hay entrega
  // real (delivered/partial); exception.occurred_at para failed/skipped.
  // Si el stop no tiene timestamp del POD aún, se omite del cálculo.
  let firstStopAt: Date | undefined;
  let lastStopAt: Date | undefined;
  let lastStopLat: number | undefined;
  let lastStopLon: number | undefined;
  const podEvents: PodEvent[] = [];
  try {
    const detail = await api.getRoute(r.id);
    const stops = detail.stops ?? [];
    stopsTotal = stops.length;
    for (const s of stops) {
      const isDone = s.status === "delivered" || s.status === "partial";
      const isFailed = s.status === "failed" || s.status === "skipped";
      if (isDone) stopsDone++;
      else if (isFailed) stopsFailed++;

      // Timestamp del POD para esta parada — preferimos la entrega real;
      // si no, el momento del rechazo. Stops pendientes (sin POD) no
      // aportan timestamp.
      const tsRaw =
        s.delivery?.delivered_at ?? s.exception?.occurred_at ?? null;
      if (!tsRaw) continue;
      const ts = new Date(tsRaw);
      if (Number.isNaN(ts.getTime())) continue;

      // Cada stop cerrado genera un evento POD para la timeline.
      if (isDone || isFailed) {
        podEvents.push({ at: ts, kind: s.status as PodEventKind });
      }

      if (!firstStopAt || ts < firstStopAt) firstStopAt = ts;
      if (!lastStopAt || ts > lastStopAt) {
        lastStopAt = ts;
        // Ubicación reportada del POD si existe; cae al lat/lon
        // planificado del stop para no perder posición.
        const podLat = s.delivery?.lat ?? s.exception?.lat ?? s.lat;
        const podLon = s.delivery?.lon ?? s.exception?.lon ?? s.lon;
        lastStopLat = podLat ?? undefined;
        lastStopLon = podLon ?? undefined;
      }
    }
    // Orden cronológico para la timeline (más temprano primero).
    podEvents.sort((a, b) => a.at.getTime() - b.at.getTime());
  } catch {
    /* ruta sin detalle accesible — se reporta con 0 stops */
  }

  // GPS DESACTIVADO — no llamamos a /api/drivers/{id}/last-gps. La
  // posición operativa del vehículo se deriva del último POD del día
  // (lastStopLat/lon arriba). lat/lon/lastGpsAt quedan undefined para
  // mantener compatibilidad con código viejo que los referencia.
  const lat: number | undefined = undefined;
  const lon: number | undefined = undefined;
  const lastGpsAt: Date | undefined = undefined;

  return {
    id: r.vehicle_id,
    plate: vehicle?.plate ?? "—",
    driverUserId: driver?.id,
    driverFullName: driver?.full_name,
    driverCode: driver?.code,
    driverPhone: driver?.phone,
    lat,
    lon,
    lastGpsAt,
    warehouseLat: warehouse?.lat,
    warehouseLon: warehouse?.lon,
    firstStopAt,
    lastStopAt,
    lastStopLat,
    lastStopLon,
    routeId: r.id,
    routeCode: r.code,
    routeStatus: r.status,
    warehouseId: r.warehouse_id,
    routeStartedAt: r.started_at ? new Date(r.started_at) : undefined,
    routeFinishedAt: r.finished_at ? new Date(r.finished_at) : undefined,
    stopsTotal,
    stopsDone,
    stopsFailed,
    stopsVisited: stopsDone + stopsFailed,
    podEvents,
  };
}

/// enrichRepartidorRow — equivalente a enrichVehicleRow pero para rutas
/// self-dispatch del repartidor (SALUD). Diferencias en el mapeo:
///   - El "sujeto" es el repartidor: `id` = route_id (no hay vehículo
///     que mantenga continuidad entre días).
///   - `plate` se rellena con el DNI para que las cards muestren un
///     identificador familiar; `driverCode` también guarda DNI.
///   - `driverFullName` = nombre del repartidor.
///   - GPS desactivado (mismo criterio que CONSUMO).
///
/// Los stops y podEvents se derivan idéntico que en CONSUMO — la UI
/// (timeline, mapa, KPIs) consume el mismo shape.
async function enrichRepartidorRow(
  r: Route & { driver_user_id: string },
  repartidorById: Map<string, User>,
  warehouseById: Map<string, Warehouse>,
): Promise<LiveVehicle> {
  const repartidor = repartidorById.get(r.driver_user_id);
  const warehouse = warehouseById.get(r.warehouse_id);

  let stopsTotal = 0, stopsDone = 0, stopsFailed = 0;
  let firstStopAt: Date | undefined;
  let lastStopAt: Date | undefined;
  let lastStopLat: number | undefined;
  let lastStopLon: number | undefined;
  const podEvents: PodEvent[] = [];
  try {
    const detail = await api.getRoute(r.id);
    const stops = detail.stops ?? [];
    stopsTotal = stops.length;
    for (const s of stops) {
      const isDone = s.status === "delivered" || s.status === "partial";
      const isFailed = s.status === "failed" || s.status === "skipped";
      if (isDone) stopsDone++;
      else if (isFailed) stopsFailed++;

      const tsRaw =
        s.delivery?.delivered_at ?? s.exception?.occurred_at ?? null;
      if (!tsRaw) continue;
      const ts = new Date(tsRaw);
      if (Number.isNaN(ts.getTime())) continue;

      if (isDone || isFailed) {
        podEvents.push({ at: ts, kind: s.status as PodEventKind });
      }
      if (!firstStopAt || ts < firstStopAt) firstStopAt = ts;
      if (!lastStopAt || ts > lastStopAt) {
        lastStopAt = ts;
        const podLat = s.delivery?.lat ?? s.exception?.lat ?? s.lat;
        const podLon = s.delivery?.lon ?? s.exception?.lon ?? s.lon;
        lastStopLat = podLat ?? undefined;
        lastStopLon = podLon ?? undefined;
      }
    }
    podEvents.sort((a, b) => a.at.getTime() - b.at.getTime());
  } catch {
    /* ruta sin detalle accesible — se reporta con 0 stops */
  }

  // El "code" del user repartidor lleva el DNI (lo seteamos en el
  // upsert). Si por algún motivo viene vacío caemos a "—" para no
  // dejar el chip sin texto.
  const dni = repartidor?.code || "—";

  return {
    // Usamos route_id como identidad del LiveVehicle SALUD — el
    // repartidor no tiene vehículo persistente y la ruta self-dispatch
    // del día es lo más estable que tenemos.
    id: r.id,
    plate: dni,
    driverUserId: repartidor?.id,
    driverFullName: repartidor?.full_name,
    driverCode: dni,
    driverPhone: repartidor?.phone,
    lat: undefined,
    lon: undefined,
    lastGpsAt: undefined,
    warehouseLat: warehouse?.lat,
    warehouseLon: warehouse?.lon,
    firstStopAt,
    lastStopAt,
    lastStopLat,
    lastStopLon,
    routeId: r.id,
    routeCode: r.code,
    routeStatus: r.status,
    warehouseId: r.warehouse_id,
    routeStartedAt: r.started_at ? new Date(r.started_at) : undefined,
    routeFinishedAt: r.finished_at ? new Date(r.finished_at) : undefined,
    stopsTotal,
    stopsDone,
    stopsFailed,
    stopsVisited: stopsDone + stopsFailed,
    podEvents,
  };
}

/// Hook con polling. Devuelve el estado de la flota + un `refresh()`
/// manual. Se auto-refresca cada `intervalMs` (default 20s). Si
/// `plannedDate` (YYYY-MM-DD) viene seteado, filtra las rutas a ese día.
/// Si `warehouseIds` está seteado, filtra a las rutas de esos warehouses
/// — usado por la vista coordinator para acotar la flota a la de un
/// supervisor específico.
///
/// `division` decide qué subconjunto de la flota mostrar:
///   - "CONSUMO" (default): vehículos planificados del día.
///   - "SALUD": rutas self-dispatch de repartidores en los últimos 3 días.
///   La UI consumidora (cards, mapa, timeline) no necesita cambios — el
///   shape `LiveVehicle` se rellena con DNI/nombre del repartidor en
///   lugar de placa/chofer.
export function useLiveFleet(
  intervalMs = 20_000,
  plannedDate?: string,
  warehouseIds?: string[],
  division: "CONSUMO" | "SALUD" = "CONSUMO",
): LiveFleetState & { refresh: () => void } {
  const [state, setState] = useState<LiveFleetState>({
    vehicles: [],
    loading: true,
    error: null,
    lastRefresh: null,
  });
  const mounted = useRef(true);

  // Estabilizamos la key del array de warehouseIds para que useCallback
  // no se re-cree cada render (nuevo array → nueva referencia).
  const warehouseIdsKey = (warehouseIds ?? []).join(",");
  const load = useCallback(async () => {
    try {
      const ids = warehouseIdsKey ? warehouseIdsKey.split(",") : undefined;
      const vehicles = await fetchFleet(plannedDate, ids, division);
      if (!mounted.current) return;
      setState({ vehicles, loading: false, error: null, lastRefresh: new Date() });
    } catch (e) {
      if (!mounted.current) return;
      setState((prev) => ({ ...prev, loading: false, error: (e as Error).message }));
    }
  }, [plannedDate, warehouseIdsKey, division]);

  useEffect(() => {
    mounted.current = true;
    load();
    const t = setInterval(load, intervalMs);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [load, intervalMs]);

  return { ...state, refresh: load };
}
